<template>
  <v-container fluid>
    <v-form ref="queryBuilderForm" v-model="validQueryInput" :lazy-validation="lazyValidateForm">
      <v-row>
        <v-col cols="9">
          <v-card>
            <v-toolbar color="primary" dark dense>
              <v-toolbar-title>
                {{ headerTitle }}
                <template v-if="queryName">[{{ queryName }}]</template>
              </v-toolbar-title>
            </v-toolbar>
            <!-- Query Input Form -->
            <query-input-form
              :initial-query-name="queryName"
              :query-name.sync="queryName"
              :selected-query-params="selectedQueryParams"
              :query-param-values="queryParamValues"
              :initial-dealer-id="dealerId"
              :dealer-id.sync="dealerId"
              v-on:remove-query-param="removeQueryParam"
              v-on:update-query-param-value="saveQueryParamChange"
            ></query-input-form>
            <!-- Button Actions -->
            <v-card-actions class="justify-center">
              <h3 v-if="isDmsTemplate">Template: {{ queryName }}</h3>
              <v-btn
                v-if="!isDmsTemplate && !query.id"
                color="blue-grey"
                class="ma-2"
                :disabled="isLoadingDataPreview"
                :dark="!isLoadingDataPreview"
                @click="saveAsTemplate"
              >
                Save As Template
                <v-icon class="pl-1">mdi-content-save</v-icon>
              </v-btn>
              <v-spacer></v-spacer>

              <v-select
                v-model="dedupeFlag"
                :items="dedupeFlags"
                hint="Dedupe Results By"
                :persistent-hint="true"
              ></v-select>
              <v-spacer></v-spacer>

              <v-btn
                v-if="!isDmsTemplate"
                color="teal"
                class="ma-2"
                :dark="!isLoadingDataPreview"
                :loading="isLoadingHtmlPreview"
                :disabled="isLoadingDataPreview"
                @click="generateHtmlPreview"
              >
                Preview Results
                <v-icon class="pl-1">mdi-magnify</v-icon>
                <template v-slot:loader>
                  <span class="custom-loader">
                    <v-icon light>mdi-cached</v-icon>
                  </span>
                </template>
              </v-btn>
              <v-btn
                v-if="!isDmsTemplate"
                color="indigo"
                class="ma-2"
                :dark="!isLoadingDataPreview"
                :disabled="isLoadingDataPreview"
                :loading="isLoadingCsvPreview"
                @click="generateCsvPreview"
              >
                Download CSV
                <v-icon class="pl-1">mdi-cloud-download</v-icon>
                <template v-slot:loader>
                  <span class="custom-loader">
                    <v-icon light>mdi-cached</v-icon>
                  </span>
                </template>
              </v-btn>
              <v-btn
                color="primary"
                class="ma-2"
                :disabled="isLoadingDataPreview"
                :dark="!isLoadingDataPreview"
                @click="handleDynamicSave"
              >
                Save
                <v-icon class="pl-1">mdi-content-save</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
          <div class="mt-4"></div>
          <query-preview-table
            v-if="queryPreviewVisible"
            :table-data="queryPreviewData"
            :table-columns="queryPreviewColumns"
            :table-counts="queryPreviewCounts"
            :table-sql="queryPreviewSql"
            :show-loading="isLoadingHtmlPreview"
            v-on:close-query-preview="toggleQueryPreviewVisibity"
          ></query-preview-table>
        </v-col>
        <v-col cols="3">
          <!-- Query Params Select -->
          <query-param-select
            v-bind:available-query-params="availableQueryParams"
            v-bind:selected-query-params="selectedQueryParams"
            v-on:select-query-param="selectQueryParam"
            v-on:remove-query-param="removeQueryParam"
            v-on:apply-dms-template="applyDmsTemplateParam"
          ></query-param-select>
        </v-col>
      </v-row>
    </v-form>
  </v-container>
</template>

<script>
import { mapState } from 'vuex'
import QueryParamSelect from './QueryParamSelect'
import QueryInputForm from './QueryInputForm'
import QueryPreviewTable from './QueryPreviewTable'

const formatDataForSave = (queryId, queryName, queryParamValues, dealerId, dedupeField) => {
  return {
    id: queryId,
    dealer_id: dealerId,
    name: queryName,
    query_params: queryParamValues || [],
    dedupe_on: dedupeField
  }
}

export default {
  name: 'query-builder',
  components: {
    'query-param-select': QueryParamSelect,
    'query-input-form': QueryInputForm,
    [QueryPreviewTable.name]: QueryPreviewTable
  },

  props: {
    headerTitle: {
      type: String,
      required: true
    },
    queryId: {
      type: [Number, String],
      required: false,
      default: null
    },
    isTemplate: {
      type: Boolean,
      required: true
    }
  },

  data: function () {
    return {
      validQueryInput: true,
      lazyValidateForm: false,
      availableQueryParams: [],
      selectedQueryParams: [],
      queryParamValues: [],
      queryName: null,
      dealerId: null,
      queryPreviewVisible: false,
      queryPreviewData: [],
      queryPreviewCounts: {},
      queryPreviewSql: '',
      queryPreviewAddressCount: null,
      isLoadingDataPreview: false,
      isLoadingHtmlPreview: false,
      isLoadingCsvPreview: false,
      dedupeFlags: [
        { text: 'None - Show All Results', value: null },
        { text: 'Customer ID', value: 'customer_id' },
        { text: 'Email', value: 'email' }
      ]
    }
  },

  computed: {
    ...mapState('QueryBuilderStore', ['queryParameters', 'query']),
    ...mapState('DealerStore', ['dealer']),
    isDmsTemplate () {
      if (this.query && this.query.type === 'DmsDatasourceTemplate') {
        return true
      } else {
        return false
      }
    },
    dedupeFlag: {
      get: function () {
        return this.$store.state.ApplicationStore.queryBuilderDedupeFlag
      },
      set: function (val) {
        return this.$store.commit('ApplicationStore/setQueryBuilderDedupeFlag', val)
      }
    }
  },

  watch: {
    // any time we set the query object, we need to update
    // the screen values with matching params...
    query: function (newQuery, oldQuery) {
      if (newQuery.id) {
        if (this.isDmsTemplate) {
          // set to current dealer - changed on backend
          this.dealerId = this.dealer.id
        } else {
          // set to dealer of dms datasource
          this.dealerId = newQuery.dealer_id
        }
        this.queryName = newQuery.name

        newQuery.params.forEach(qp => {
          this.selectQueryParam(qp.name)
          this.addQueryParamValues(qp.name, qp)
        })
      } else {
        this.dealerId = this.dealer.id
      }
    },
    dealer: function (newDealer, oldDealer) {
      this.dealerId = newDealer.id
    },
    isLoadingHtmlPreview: function (newVal, oldVal) {
      this.isLoadingDataPreview = newVal // make the data loading mirror this setting to disable/enable other buttons
      if (newVal) this.queryPreviewVisible = true
    },
    isLoadingCsvPreview: function (newVal, oldVal) {
      this.isLoadingDataPreview = newVal // make the data loading mirror this setting to disable/enable other buttons
    },
    queryId: function (newVal, oldVal) {
      if (newVal) {
        this.loadQueryBuilder()
      }
    }
  },
  methods: {
    toggleQueryPreviewVisibity: function () {
      this.queryPreviewVisible = !this.queryPreviewVisible
    },
    selectQueryParam: function (queryParamName, queryParamValue = null) {
      this._addSelectedQueryParam(queryParamName)
      this.addQueryParamValues(queryParamName, queryParamValue)
    },
    removeQueryParam: function (queryParamName) {
      this.removeQueryParamValues(queryParamName)
      this._removeSelectedQueryParam(queryParamName)
    },

    resetPreview: function () {
      this.queryPreviewData = []
      this.queryPreviewColumns = []
      this.queryPreviewCounts = {}
      this.queryPreviewSql = ''
    },
    resetAll: function () {
      this.availableQueryParams = []
      this.selectedQueryParams = []
      this.queryParamValues = []
      this.resetPreview()
      // reset the store query if there is one
      this.$store.commit('QueryBuilderStore/storeQuery', {})
    },
    // queryParamValue is what we want the initial values to be set to
    // defaults to {name: queryParamName} if no value set
    addQueryParamValues: function (queryParamName, queryParamValue) {
      const queryParamData = Object.assign({}, queryParamValue, {
        name: queryParamName
      })
      const idx = this.queryParamValues.findIndex(qparam => {
        return qparam.name === queryParamName
      })
      if (idx > -1) {
        this.$set(this.queryParamValues, idx, queryParamData)
      } else {
        this.queryParamValues.splice(
          this.queryParamValues.length,
          0,
          queryParamData
        )
      }
    },
    saveQueryParamChange: function (queryParamData) {
      // find the query param value, noting it's index
      // then set that value...
      const qpIndex = this.queryParamValues.findIndex(
        qp => qp.name === queryParamData.name
      )
      if (qpIndex !== -1) {
        const qpVal = this.queryParamValues[qpIndex]
        const qpData = { [queryParamData.key]: queryParamData.value }
        this.$set(
          this.queryParamValues,
          qpIndex,
          Object.assign({}, qpVal, qpData)
        )
      } else {
        this.$logger.trace('Unknown Query Param Data Values: ', queryParamData)
      }
    },
    removeQueryParamValues: function (queryParamName) {
      this._removeQueryParamFromArray(queryParamName, this.queryParamValues)
    },
    applyDmsTemplateParam: function (selectedTemplate) {
      this.$logger.trace('Selected Dms Template', selectedTemplate)
      selectedTemplate.params.forEach(param => {
        this.selectQueryParam(param.name, param)
      })
    },
    handleDynamicSave () {
      // handle case where one save button but may be a DmsDatsource or a DmsDatasourceTemplate
      if (this.isDmsTemplate) {
        this.saveAsTemplate()
      } else {
        this.validateAndSubmit()
      }
    },
    validateAndSubmit () {
      if (this.$refs.queryBuilderForm.validate()) {
        this.saveQuery(false)
      }
    },
    saveAsTemplate () {
      this.saveQuery(true)
    },
    saveQuery: function (isTemplate) {
      const queryParamData = formatDataForSave(
        this.queryId,
        this.queryName,
        this.queryParamValues,
        this.dealerId,
        this.dedupeFlag
      )
      const saveAction = isTemplate ? 'QueryBuilderStore/saveTemplate' : 'QueryBuilderStore/saveQuery'
      this.$store
        .dispatch(saveAction, queryParamData)
        .then(query => {
          this.$emit('save', query)
        })
        .catch(error => {
          this.$logger.error(error.message)
          this.$flash({
            showClose: true,
            message: `Error Saving Dms Datsource! ${error.message}`,
            type: 'error',
            duration: 0
          })
        })
    },

    generateHtmlPreview: function () {
      if (!this.$refs.queryBuilderForm.validate()) {
        return
      }

      this.isLoadingHtmlPreview = true
      this.resetPreview()
      const previewParams = formatDataForSave(
        this.queryId,
        this.queryName,
        this.queryParamValues,
        this.dealerId,
        this.dedupeFlag
      )
      this.$store
        .dispatch('QueryBuilderStore/fetchPreviewData', previewParams)
        .then(response => {
          this.queryPreviewData = response.data.data
          this.queryPreviewColumns = response.data.metadata.fields
          this.queryPreviewCounts = {
            total: response.data.metadata.total_count,
            email: response.data.metadata.email_count,
            address: response.data.metadata.address_count
          }
          this.queryPreviewSql = response.data.metadata.sql
          this.isLoadingHtmlPreview = false
        })
        .catch(error => {
          this.$logger.error('Error retrieving preview data!', error.message)
          this.isLoadingHtmlPreview = false
          this.$flash({
            showClose: true,
            message: `Error retrieving preview data! ${error.message}`,
            type: 'error',
            duration: 0
          })
        })
    },
    generateCsvPreview: function () {
      if (!this.$refs.queryBuilderForm.validate()) {
        return
      }

      this.isLoadingCsvPreview = true
      const previewParams = formatDataForSave(
        this.queryId,
        this.queryName,
        this.queryParamValues,
        this.dealerId,
        this.dedupeFlag
      )
      previewParams.output_format = 'csv'

      this.$store
        .dispatch('QueryBuilderStore/fetchPreviewData', previewParams)
        .then(response => {
          const csvUrl = response.data.data.csv_url
          if (!csvUrl) {
            this.$flash({
              showClose: true,
              message: 'No CSV data was returned from server!',
              type: 'error',
              duration: 0
            })
            this.isLoadingCsvPreview = false
          } else {
            const downloadLink = document.createElement('a')
            downloadLink.setAttribute('href', csvUrl)
            downloadLink.setAttribute('style', 'display:none')
            downloadLink.setAttribute('target', '_blank')
            document.body.append(downloadLink)

            this.isLoadingCsvPreview = false

            setTimeout(function () {
              downloadLink.click()
              downloadLink.remove()
            }, null)
          }
        })
        .catch(error => {
          this.$logger.error('Error retrieving preview data!', error.message)
          this.isLoadingCsvPreview = false
          this.$flash({
            showClose: true,
            message: `Error retrieving preview data! ${error.message}`,
            type: 'error',
            duration: 5000
          })
        })
    },

    // sorta private methods

    _getQueryParameterData: function (queryParamName) {
      return this.queryParameters.find(qParam => {
        return qParam.name === queryParamName
      })
    },
    _addQueryParamToArray: function (queryParamName, array) {
      if (queryParamName && queryParamName !== -1) {
        const paramData = this._getQueryParameterData(queryParamName)
        if (!paramData) {
          this.$logger.trace('Not a valid query parameter', queryParamName)
          throw new Error(`Not a valid query parameter: ${queryParamName}`)
        }
        const idx = array.findIndex(qparam => {
          return qparam.name === queryParamName
        })
        if (idx > -1) {
          this.$set(array, idx, paramData)
        } else {
          array.splice(0, 0, paramData)
        }
      }
    },
    _removeQueryParamFromArray: function (queryParamName, array) {
      if (queryParamName) {
        const idx = array.findIndex(queryParam => {
          return queryParam.name === queryParamName
        })
        if (idx > -1) {
          array.splice(idx, 1)
        }
      }
    },
    _addSelectedQueryParam: function (queryParamName) {
      this._addQueryParamToArray(queryParamName, this.selectedQueryParams)
    },
    _removeSelectedQueryParam: function (queryParamName) {
      this._removeQueryParamFromArray(queryParamName, this.selectedQueryParams)
    },
    _addAvailableQueryParams: function (queryParamName) {
      this._addQueryParamToArray(queryParamName, this.availableQueryParams)
    },
    _removeAvailableQueryParams: function (queryParamName) {
      this._removeQueryParamFromArray(
        queryParamName,
        this.availableQueryParams
      )
    },
    loadQueryBuilder () {
      this.resetAll() // everyone out of the pool
      this.$store
        .dispatch('QueryBuilderStore/fetchQueryParameters')
        .then(queryParams => {
          this.availableQueryParams = queryParams
        })
        .then(() => {
          const loadAction = this.isTemplate ? 'QueryBuilderStore/getTemplate' : 'QueryBuilderStore/getQuery'
          if (this.queryId) {
            this.$logger.trace('Setting up for Edit Query Builder ' + this.queryId)
            return this.$store.dispatch(
              loadAction,
              this.queryId
            )
          } else {
            this.$logger.trace('Setting up for New Query Builder')
            return Promise.resolve('ok')
          }
        })
        .catch(error => {
          this.$logger.error('Error loading query or query params.', error.message)
          this.$flash({
            showClose: true,
            message: `Error loading query or query params. ${error.message}`,
            type: 'error',
            duration: 0
          })
        })
    }
  },
  mounted: function () {
    this.loadQueryBuilder()
  }
}
</script>
